import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, CheckCircle2, MessageSquare, Loader2, Clock, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { formatApiError } from '@/components/ProtectedRoute';

const STATUS_LABELS = {
  new:      { label: 'Nouveau',  color: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  called:   { label: 'Rappelé',   color: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  emailed:  { label: 'Répondu par mail', color: 'bg-blue-500/15 text-blue-700 border-blue-500/30' },
  other:    { label: 'Autre',     color: 'bg-purple-500/15 text-purple-700 border-purple-500/30' },
  archived: { label: 'Archivé',   color: 'bg-gray-300/40 text-gray-600 border-gray-400/30' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/**
 * Re-usable list of contact requests with status update buttons.
 * Used on the admin dashboard (only_new=true) and in /admin/analytics (all recent).
 */
export function ContactRequestsList({ onlyNew = false, limit = 20, title = 'Demandes récentes' }) {
  const [items, setItems] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/contact/list`, {
        params: { limit, only_new: onlyNew ? 'true' : 'false' },
      });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [onlyNew, limit]);

  const updateStatus = async (id, status) => {
    setUpdating(id + status);
    try {
      await axios.patch(`${API}/admin/contact/${id}`, { status });
      toast.success(`Marqué « ${STATUS_LABELS[status]?.label || status} »`);
      // optimistic update
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, handled_status: status } : it));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  const visible = onlyNew && items
    ? items.filter((i) => (i.handled_status || 'new') === 'new')
    : items;

  return (
    <div data-testid="contact-requests-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-dark">{title}</h3>
          {visible !== null && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {visible.length} {onlyNew ? 'demande(s) à traiter' : 'demande(s) au total'}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2 rounded-xl" data-testid="contact-list-refresh">
          <Loader2 className={`h-3.5 w-3.5 ${items === null ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-10 px-4 bg-accent/30 rounded-xl border border-dashed border-border" data-testid="contact-list-empty">
          <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-dark">
            {onlyNew ? 'Aucune nouvelle demande à traiter' : 'Aucune demande pour le moment'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Tu es à jour ✨</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="contact-list-items">
          <AnimatePresence>
            {visible.map((c) => {
              const status = c.handled_status || 'new';
              const isHandled = status !== 'new';
              const meta = STATUS_LABELS[status] || STATUS_LABELS.new;
              return (
                <motion.li
                  key={c.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`contact-item-${c.id}`}
                  className={`relative rounded-xl border p-4 transition-all ${
                    isHandled
                      ? 'bg-emerald-50/50 border-emerald-500/20'
                      : 'bg-white border-border hover:border-primary/40 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-dark text-sm truncate">{c.name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.color} uppercase tracking-wider`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {c.phone && (
                          <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </a>
                        )}
                        {c.email && !c.email.includes('+') && !c.email.endsWith('@cfsc.fr') && (
                          <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-primary transition-colors truncate max-w-[200px]">
                            <Mail className="h-3 w-3" /> {c.email}
                          </a>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {fmtDate(c.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {c.message && (
                    <div className="mb-3 text-xs text-dark/80 bg-accent/40 rounded-lg p-3 leading-relaxed flex gap-2">
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                      <span className="line-clamp-3">{c.message}</span>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!isHandled ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => updateStatus(c.id, 'called')}
                        disabled={updating === c.id + 'called'}
                        data-testid={`contact-mark-called-${c.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-1.5 h-8 text-xs px-3"
                      >
                        <Phone className="h-3 w-3" /> Rappelé
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(c.id, 'emailed')}
                        disabled={updating === c.id + 'emailed'}
                        data-testid={`contact-mark-emailed-${c.id}`}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg gap-1.5 h-8 text-xs px-3"
                      >
                        <Mail className="h-3 w-3" /> Répondu mail
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(c.id, 'other')}
                        disabled={updating === c.id + 'other'}
                        data-testid={`contact-mark-other-${c.id}`}
                        className="rounded-lg gap-1.5 h-8 text-xs px-3"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Autre / Traité
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateStatus(c.id, 'archived')}
                        disabled={updating === c.id + 'archived'}
                        data-testid={`contact-mark-archived-${c.id}`}
                        className="rounded-lg gap-1.5 h-8 text-xs px-3 text-muted-foreground hover:text-dark"
                      >
                        <Archive className="h-3 w-3" /> Archiver
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5 font-semibold">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Traité {c.handled_at ? `· ${fmtDate(c.handled_at)}` : ''}
                      </span>
                      <button
                        onClick={() => updateStatus(c.id, 'new')}
                        disabled={updating === c.id + 'new'}
                        data-testid={`contact-mark-new-${c.id}`}
                        className="text-[11px] text-muted-foreground hover:text-dark underline-offset-2 hover:underline"
                      >
                        Remettre en attente
                      </button>
                    </div>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}
